import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import { categoryActions } from '../../action/categoryaction';
import Swal from 'sweetalert2';
import PropTypes from 'prop-types';

const AddCategory = ({ onSuccess }) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [parentCategories, setParentCategories] = useState([]);
    const [categoryData, setCategoryData] = useState({
        category_name: '',
        description: '',
        parent_id: null,
        status: 'Active'
    });
    const [errors, setErrors] = useState({});

    // Fetch parent categories for dropdown
    useEffect(() => {
        const fetchParentCategories = async () => {
            try {
                const response = await dispatch(categoryActions.fetchParentCategories());
                if (response.success && response.data) {
                    const options = response.data.parent_categories?.map(cat => ({
                        value: cat.id,
                        label: cat.category_name
                    })) || [];
                    setParentCategories(options);
                }
            } catch (error) {
                console.error('Error fetching parent categories:', error);
            }
        };
        fetchParentCategories();
    }, [dispatch]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCategoryData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleParentChange = (selectedOption) => {
        setCategoryData(prev => ({
            ...prev,
            parent_id: selectedOption ? selectedOption.value : null
        }));
        
        if (errors.parent_id) {
            setErrors(prev => ({
                ...prev,
                parent_id: ''
            }));
        }
    };

    const handleStatusChange = (e) => {
        setCategoryData(prev => ({
            ...prev,
            status: e.target.checked ? 'Active' : 'InActive'
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!categoryData.category_name.trim()) {
            newErrors.category_name = 'Category name is required';
        } else if (categoryData.category_name.length < 2) {
            newErrors.category_name = 'Category name must be at least 2 characters';
        } else if (categoryData.category_name.length > 100) {
            newErrors.category_name = 'Category name cannot exceed 100 characters';
        }
        
        if (categoryData.description && categoryData.description.length > 500) {
            newErrors.description = 'Description cannot exceed 500 characters';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setLoading(true);
        try {
            const categoryToSubmit = {
                ...categoryData,
                created_by: 1 // Default admin user ID
            };
            
            const response = await dispatch(categoryActions.createCategory(categoryToSubmit));
            
            if (response.success) {
                // Close modal
                const closeBtn = document.getElementById('closeAddCategoryModal');
                if (closeBtn) closeBtn.click();
                
                // Reset form
                setCategoryData({
                    category_name: '',
                    description: '',
                    parent_id: null,
                    status: 'Active'
                });
                
                // Show success message
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Category created successfully',
                    confirmButtonColor: '#00ff00',
                    timer: 2000
                });
                
                // Refresh category list
                if (onSuccess) {
                    onSuccess();
                }
            }
        } catch (error) {
            console.error('Error creating category:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setCategoryData({
            category_name: '',
            description: '',
            parent_id: null,
            status: 'Active'
        });
        setErrors({});
    };

    return (
        <div className="modal fade" id="add-category">
            <div className="modal-dialog modal-dialog-centered custom-modal-two">
                <div className="modal-content">
                    <div className="modal-header border-0 custom-modal-header">
                        <div className="page-title">
                            <h4>Add New Category</h4>
                        </div>
                        <button
                            type="button"
                            className="close"
                            data-bs-dismiss="modal"
                            id="closeAddCategoryModal"
                            aria-label="Close"
                            onClick={handleClose}
                        >
                            <span aria-hidden="true">×</span>
                        </button>
                    </div>
                    
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-lg-12 col-sm-6 col-12">
                                    <div className="input-blocks">
                                        <label>Category Name <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.category_name ? 'is-invalid' : ''}`}
                                            name="category_name"
                                            value={categoryData.category_name}
                                            onChange={handleInputChange}
                                            placeholder="Enter category name"
                                            disabled={loading}
                                        />
                                        {errors.category_name && (
                                            <div className="invalid-feedback d-block">
                                                {errors.category_name}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                
                                <div className="col-lg-12 col-sm-6 col-12">
                                    <div className="input-blocks">
                                        <label>Parent Category</label>
                                        <Select
                                            className={`react-select ${errors.parent_id ? 'is-invalid' : ''}`}
                                            classNamePrefix="select"
                                            options={parentCategories}
                                            placeholder="Select parent category (optional)"
                                            value={parentCategories.find(opt => opt.value === categoryData.parent_id)}
                                            onChange={handleParentChange}
                                            isClearable={true}
                                            isDisabled={loading}
                                        />
                                        <small className="text-muted">Leave empty to create a main category</small>
                                    </div>
                                </div>
                                
                                <div className="col-lg-12 col-sm-6 col-12">
                                    <div className="input-blocks">
                                        <label>Description</label>
                                        <textarea
                                            className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                                            name="description"
                                            value={categoryData.description}
                                            onChange={handleInputChange}
                                            placeholder="Enter category description"
                                            rows="3"
                                            disabled={loading}
                                        />
                                        {errors.description && (
                                            <div className="invalid-feedback d-block">
                                                {errors.description}
                                            </div>
                                        )}
                                        <small className="text-muted">
                                            {categoryData.description.length}/500 characters
                                        </small>
                                    </div>
                                </div>
                                
                                <div className="col-lg-12 col-sm-6 col-12">
                                    <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                                        <span className="status-label">Status</span>
                                        <input
                                            type="checkbox"
                                            id="add_status"
                                            className="check"
                                            checked={categoryData.status === 'Active'}
                                            onChange={handleStatusChange}
                                            disabled={loading}
                                        />
                                        <label htmlFor="add_status" className="checktoggle">
                                            {categoryData.status}
                                        </label>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="modal-footer-btn mt-4">
                                <button 
                                    type="button" 
                                    className="btn btn-cancel me-2" 
                                    data-bs-dismiss="modal"
                                    onClick={handleClose}
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    className="btn btn-submit" 
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Creating...
                                        </>
                                    ) : (
                                        'Create Category'
                                    )}
                                </button>
                            </div>
                        </div>
                    </form>
                    
                    {/* Modal close event handler */}
                    <script dangerouslySetInnerHTML={{
                        __html: `
                            document.getElementById('add-category').addEventListener('hidden.bs.modal', function() {
                                window.dispatchEvent(new CustomEvent('modalClosed', { detail: 'add-category' }));
                            });
                        `
                    }} />
                </div>
            </div>
        </div>
    );
};
AddCategory.propTypes = {
  onSuccess: PropTypes.func.isRequired,
};
export default AddCategory;
